// ============================================================================
// ChallanGenerationView.jsx
// "Challan Generation" card — browse fee assignments and select students.
// Printing (preview + bulk print) now lives in ChallanPrintingView.jsx;
// selection state is owned by FeeManagement.js so it survives the move
// from Generation to Printing.
// ============================================================================

import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders } from "../../../utils/api";
import { FEE_CATEGORIES, FEE_COMPONENTS } from "./feeConstants";
import { assignFeeToStudent, deleteFeeEntry } from "./feeApi";
import { challansByFeeKey, updateGeneratedChallan } from "./challanApi";
import {
  sectionsForClassSession,
  uniqueClassNames,
} from "../../../utils/campusClasses";

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtPKR     = (n) => `PKR ${Number(n || 0).toLocaleString("en-PK")}`;
const fmtClass   = (c) => c ? c.replace(/Grade/i, "Class").trim() : "—";
const fmtSection = (s) => s ? s.replace(/Section\s*/i, "").trim() : "—";
const loadPrevDues = (sid) => { try { return JSON.parse(localStorage.getItem(`previous_dues_${sid}`) || "[]").filter(d => !d.paid).reduce((s, d) => s + d.amount, 0); } catch { return 0; } };
const defaultComponentsForCategory = (catId) => {
  const map = {
    monthly: ["tuition"],
    exam: ["examination", "tuition"],
    sports: ["sports"],
    library: ["library"],
    transport: ["transport"],
    hostel: ["hostel"],
  };
  return (map[catId] || []).map((id) => {
    const fc = FEE_COMPONENTS.find((c) => c.id === id);
    return fc ? { componentId: fc.id, componentLabel: fc.label, amount: fc.defaultValue } : null;
  }).filter(Boolean);
};
const buildComponentsFromCategories = (catIds, fallback = []) => {
  const seen = new Set();
  const existing = new Map((fallback || []).map((c) => [String(c.componentId || c.componentLabel).toLowerCase(), c]));
  return catIds.flatMap(defaultComponentsForCategory).filter((comp) => {
    const key = comp.componentLabel.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).map((comp) => {
    const match = existing.get(String(comp.componentId).toLowerCase()) || existing.get(comp.componentLabel.toLowerCase());
    return match ? { ...comp, amount: match.amount } : comp;
  });
};
const buildCategoryMeta = (catIds) => {
  const labels = catIds.map((id) => FEE_CATEGORIES.find((c) => c.id === id)?.label || id).filter(Boolean);
  return {
    categoryId: catIds.length > 1 ? "custom" : (catIds[0] || ""),
    categoryLabel: labels.join(" + "),
    categoryPrefix: catIds.length > 1 ? "MIX" : (FEE_CATEGORIES.find((c) => c.id === catIds[0])?.prefix || "FEE"),
  };
};

// ── Design tokens (matches ALS Portal brand palette in src/index.css) ────────
const C = {
  navy: "#0f1d36", navyMid: "#1c3461", blue: "#2a4a7f",
  blueSoft: "rgba(30,58,95,0.10)", amber: "#8a6c1f", amberSoft: "rgba(196,154,42,0.15)",
  green: "#2e7d32", greenSoft: "rgba(46,125,50,0.10)", greenBdr: "rgba(46,125,50,0.35)",
  red: "#c41e24", redSoft: "rgba(196,30,36,0.08)",
  slate: "#5a5e6a", slateLight: "#8e92a0", border: "#e8e9ed",
  bg: "#f8f9fc", white: "#ffffff", text: "#1a1d23",
};

const S = {
  card:      { background: C.white, borderRadius: "12px", border: `1px solid ${C.border}`, marginBottom: "16px", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" },
  cardHead:  { padding: "16px 20px", background: C.navy, display: "flex", justifyContent: "space-between", alignItems: "center" },
  cardTitle: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: "15px", fontWeight: 700, color: C.white },
  filterRow: { padding: "14px 20px", display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-end", borderBottom: `1px solid ${C.border}`, background: C.bg },
  fg:        { display: "flex", flexDirection: "column", gap: "5px", minWidth: "130px" },
  label:     { fontSize: "11px", fontWeight: 700, color: C.slate, textTransform: "uppercase", letterSpacing: "0.5px" },
  select:    { padding: "9px 12px", borderRadius: "8px", border: `1.5px solid ${C.border}`, fontSize: "13px", background: C.white, cursor: "pointer", color: C.text },
  input:     { padding: "9px 12px", borderRadius: "8px", border: `1.5px solid ${C.border}`, fontSize: "13px", background: C.white, outline: "none", color: C.text, width: "100%", boxSizing: "border-box" },
  table:     { width: "100%", borderCollapse: "collapse" },
  th:        { textAlign: "left", padding: "10px 16px", fontSize: "11px", fontWeight: 700, color: C.slate, background: C.bg, borderBottom: `2px solid ${C.border}`, textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap" },
  td:        { padding: "12px 16px", fontSize: "13px", color: C.text, borderBottom: `1px solid ${C.border}`, verticalAlign: "middle" },
  badge:     (bg, fg) => ({ display: "inline-block", padding: "2px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 700, background: bg, color: fg }),
  btn:       (bg, fg, border) => ({ padding: "8px 16px", borderRadius: "8px", border: border || "none", background: bg, color: fg, fontSize: "13px", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap" }),
  checkbox:  { width: "15px", height: "15px", cursor: "pointer", accentColor: C.blue },
};

// ── Main ChallanGenerationView ────────────────────────────────────────────────
const ChallanGenerationView = ({ students = [], feeAssignments = {}, selected, onSelectedChange, onUpdateDue, onFeesChanged, onOpenBulkPage, campusId }) => {
  const navigate = useNavigate();
  const [sessionFilter,  setSessionFilter]  = React.useState("all");
  const [sessions,       setSessions]       = React.useState([]);
  const [campusClasses,  setCampusClasses]  = React.useState([]);
  const [classFilter,    setClassFilter]    = React.useState("all");
  const [sectionFilter,  setSectionFilter]  = React.useState("all");
  // multi-select: Set of category ids, empty means "all"
  const [categoryFilters, setCategoryFilters] = useState(new Set());
  const [catDropOpen,     setCatDropOpen]     = useState(false);
  const [searchQ,        setSearchQ]        = React.useState("");
  const [page,           setPage]           = React.useState(1);
  const [perPage,        setPerPage]        = React.useState(10);
  const catDropRef = useRef(null);
  useEffect(() => {
    if (!catDropOpen) return;
    const handle = (e) => { if (catDropRef.current && !catDropRef.current.contains(e.target)) setCatDropOpen(false); };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [catDropOpen]);
  const [challanMap,     setChallanMap]     = useState(new Map());
  const [editChallan,    setEditChallan]    = useState(null); // { item, challan, selectedCats, dueDate, components, netPayable }

  useEffect(() => {
    let alive = true;
    const scope = campusId ? `?campus_id=${campusId}` : "";

    Promise.all([
      fetch(`${API_BASE_URL}/api/sessions${scope}`, { headers: getAuthHeaders() })
        .then((response) => (response.ok ? response.json() : []))
        .catch(() => []),
      fetch(`${API_BASE_URL}/api/classes${scope}`, { headers: getAuthHeaders() })
        .then((response) => (response.ok ? response.json() : []))
        .catch(() => []),
    ]).then(([sessionData, classData]) => {
      if (!alive) return;
      setSessions(Array.isArray(sessionData) ? sessionData : []);
      setCampusClasses(Array.isArray(classData) ? classData : []);
    });

    return () => { alive = false; };
  }, [campusId]);

  const refreshChallanMap = useCallback(async () => {
    const map = await challansByFeeKey();
    setChallanMap(map);
  }, []);

  useEffect(() => { refreshChallanMap(); }, [refreshChallanMap, feeAssignments]);
  useEffect(() => {
    const handler = () => refreshChallanMap();
    window.addEventListener("challans-generated", handler);
    return () => window.removeEventListener("challans-generated", handler);
  }, [refreshChallanMap]);

  const getChallanForItem = (student, feeStructure) =>
    challanMap.get(`${student.id}|${feeStructure.categoryId}_${feeStructure.month}`) || null;

  // Flatten all fee assignments into a list of { student, feeStructure }
  const allFeeItems = useMemo(() => {
    const items = [];
    students.forEach(student => {
      const fees = feeAssignments[student.id] || feeAssignments[String(student.id)] || {};
      Object.values(fees).forEach(feeStructure =>
        items.push({
          id: `${student.id}-${feeStructure.categoryId}-${feeStructure.month || ""}`,
          student,
          feeStructure,
        })
      );
    });
    return items;
  }, [students, feeAssignments]);

  // Unique fee-category ids from assignments (expand multi-select / custom combos)
  const categories = useMemo(() => {
    const unique = new Set();
    allFeeItems.forEach(({ feeStructure }) => {
      const selected = feeStructure.selectedCategories;
      if (Array.isArray(selected) && selected.length) {
        selected.forEach((id) => { if (id) unique.add(id); });
      } else if (feeStructure.categoryId) {
        unique.add(feeStructure.categoryId);
      }
    });
    // Prefer known FEE_CATEGORIES order; append any unknown ids last
    const knownOrder = FEE_CATEGORIES.map((c) => c.id).filter((id) => unique.has(id));
    const extras = Array.from(unique).filter((id) => !knownOrder.includes(id));
    return ["all", ...knownOrder, ...extras];
  }, [allFeeItems]);

  const sessionNameForItem = useCallback(
    ({ student, feeStructure }) =>
      feeStructure.session ||
      student.academic_session ||
      feeStructure.academicYear ||
      "",
    [],
  );

  const classes = useMemo(() => {
    if (campusClasses.length && sessionFilter !== "all") {
      const selectedSession = sessions.find((session) => session.name === sessionFilter);
      const names = uniqueClassNames(campusClasses, {
        sessionId: selectedSession?.id,
      });
      return ["all", ...names];
    }

    const pool = sessionFilter === "all"
      ? allFeeItems
      : allFeeItems.filter((item) => sessionNameForItem(item) === sessionFilter);
    const unique = new Set(pool.map(i => i.student.class).filter(Boolean));
    return ["all", ...Array.from(unique).sort()];
  }, [allFeeItems, sessionFilter, sessionNameForItem, campusClasses, sessions]);

  // Sections available for the selected class (or all classes)
  const sections = useMemo(() => {
    if (campusClasses.length && sessionFilter !== "all") {
      const selectedSession = sessions.find((session) => session.name === sessionFilter);
      const values = sectionsForClassSession(campusClasses, {
        sessionId: selectedSession?.id,
        className: classFilter === "all" ? "" : classFilter,
        students: students,
        formatSection: fmtSection,
      });
      return ["all", ...values];
    }

    const pool = allFeeItems.filter((item) =>
      (sessionFilter === "all" || sessionNameForItem(item) === sessionFilter) &&
      (classFilter === "all" || (item.student.class || "") === classFilter)
    );
    const unique = new Set(pool.map(i => fmtSection(i.student.section)).filter(s => s && s !== "—"));
    return ["all", ...Array.from(unique).sort()];
  }, [allFeeItems, sessionFilter, classFilter, sessionNameForItem, campusClasses, sessions, students]);

  const getCategoryLabel = (id) =>
    FEE_CATEGORIES.find((c) => c.id === id)?.label ||
    allFeeItems.find((i) => i.feeStructure.categoryId === id)?.feeStructure.categoryLabel ||
    id;

  const itemMatchesCategoryFilter = (feeStructure) => {
    if (categoryFilters.size === 0) return true;
    const selected = feeStructure.selectedCategories;
    if (Array.isArray(selected) && selected.length) {
      return selected.some((id) => categoryFilters.has(id));
    }
    return categoryFilters.has(feeStructure.categoryId);
  };

  const filtered = useMemo(() => allFeeItems.filter(({ student, feeStructure }) => {
    const q = searchQ.toLowerCase().trim();

    const studentClass = student.class || student.class_name || "";
    const rollNo       = student.rollNo || student.student_id || student.id || "";
    const studentName  = student.name || "";
    const feeLabel     = feeStructure.categoryLabel || "";
    const studentSection = fmtSection(student.section);

    const sessionName = sessionNameForItem({ student, feeStructure });
    const sessionMatch = sessionFilter === "all" || sessionName === sessionFilter;

    const classMatch = classFilter === "all" || studentClass === classFilter;
    const sectionMatch = sectionFilter === "all" || studentSection === sectionFilter;
    const categoryMatch = itemMatchesCategoryFilter(feeStructure);
    const searchMatch = !q || (
      studentName.toLowerCase().includes(q) ||
      String(rollNo).toLowerCase().includes(q) ||
      feeLabel.toLowerCase().includes(q)
    );

    return sessionMatch && classMatch && sectionMatch && categoryMatch && searchMatch;
  }), [allFeeItems, sessionFilter, classFilter, sectionFilter, categoryFilters, searchQ, sessionNameForItem]);

  // Reset to first page whenever the filtered set changes.
  React.useEffect(() => { setPage(1); }, [sessionFilter, classFilter, sectionFilter, categoryFilters, searchQ, perPage]);

  React.useEffect(() => {
    setClassFilter("all");
    setSectionFilter("all");
  }, [sessionFilter]);

  // When class changes, reset section if it no longer exists in that class
  React.useEffect(() => {
    if (sectionFilter !== "all" && !sections.includes(sectionFilter)) {
      setSectionFilter("all");
    }
  }, [classFilter, sections, sectionFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage   = Math.min(page, totalPages);
  const pageItems  = filtered.slice((safePage - 1) * perPage, safePage * perPage);

  const allSelected      = filtered.length > 0 && filtered.every(i => selected.has(i.id));
  const someSelected     = filtered.some(i => selected.has(i.id));
  const selectedFiltered = filtered.filter(i => selected.has(i.id));

  const handleBulkGenerate = () => {
    const poolStudents = someSelected
      ? [...new Map(selectedFiltered.map((i) => [i.student.id, i.student])).values()]
      : classFilter === "all"
        ? []
        : [...new Map(
            allFeeItems
              .filter(({ student }) => {
                const studentClass = student.class || "";
                const studentSection = fmtSection(student.section);
                return studentClass === classFilter && (sectionFilter === "all" || studentSection === sectionFilter);
              })
              .map((i) => [i.student.id, i.student])
          ).values()];

    if (!poolStudents.length) {
      alert("Please select a Class (and optional Section) or check specific students first.");
      return;
    }

    if (onOpenBulkPage) {
      onOpenBulkPage({
        students: poolStudents,
        classFilter: classFilter !== "all" ? classFilter : poolStudents[0]?.class,
        sectionFilter: sectionFilter !== "all" ? sectionFilter : null,
      });
      return;
    }

    alert("Bulk generation page is not available.");
  };

  const handleSaveChallanEdit = async () => {
    if (!editChallan) return;
    const { item, challan, selectedCats, dueDate, components } = editChallan;
    const { student, feeStructure } = item;
    const meta = buildCategoryMeta([...selectedCats]);

    await deleteFeeEntry(student.id, feeStructure.categoryId, feeStructure.month);
    const savedFee = await assignFeeToStudent({
      studentId: student.id,
      studentName: student.name,
      studentClass: student.class,
      studentRollNo: student.rollNo,
      studentSection: student.section,
      categoryId: meta.categoryId,
      categoryLabel: meta.categoryLabel || feeStructure.categoryLabel,
      categoryPrefix: meta.categoryPrefix,
      selectedCategories: [...selectedCats],
      components: (components || []).map((c) => ({ ...c, amount: Number(c.amount) || 0 })),
      academicYear: feeStructure.academicYear,
      month: feeStructure.month,
      dueDate: String(dueDate),
      concession: feeStructure.concession || null,
    });

    if (challan) {
      await updateGeneratedChallan(challan.id, {
        feeAssignmentKey: `${savedFee.categoryId}_${savedFee.month}`,
        categoryId: savedFee.categoryId,
        categoryLabel: savedFee.categoryLabel,
        categoryPrefix: savedFee.categoryPrefix,
        dueDate: String(dueDate),
        selectedCategories: [...selectedCats],
        components: savedFee.components,
        subtotal: savedFee.subtotal,
        netPayable: savedFee.netPayable,
        month: savedFee.month,
      });
    }

    if (onUpdateDue) await onUpdateDue(student.id, savedFee.categoryId, savedFee.month, dueDate);
    if (onFeesChanged) await onFeesChanged(student.id);

    await refreshChallanMap();
    setEditChallan(null);
  };

  const toggleOne = (id) => onSelectedChange(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => {
    if (allSelected) onSelectedChange(prev => { const n = new Set(prev); filtered.forEach(i => n.delete(i.id)); return n; });
    else             onSelectedChange(prev => { const n = new Set(prev); filtered.forEach(i => n.add(i.id)); return n; });
  };
  const clearSelection = () => onSelectedChange(new Set());

  if (allFeeItems.length === 0) return (
    <div style={{ ...S.card, padding: "60px", textAlign: "center", color: C.slate }}>
      <div style={{ fontSize: "36px", marginBottom: "12px" }}>🧾</div>
      <div style={{ fontWeight: 700, fontSize: "15px", color: C.text, marginBottom: "6px" }}>No Fee Assignments Found</div>
      <div style={{ fontSize: "13px" }}>Go to the <strong>Fee Management</strong> card and assign fee categories to students first.</div>
    </div>
  );

  return (
    <>
      {/* Filters card */}
      <div style={{ ...S.card, overflow: "visible" }}>
        <div style={S.cardHead}>
          <span style={S.cardTitle}>Challan Generation</span>
          <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.7)", background: "rgba(255,255,255,0.12)", padding: "3px 10px", borderRadius: "20px", fontWeight: 600 }}>{allFeeItems.length} fee assignments</span>
        </div>

        <div style={S.filterRow}>
          <div style={S.fg}>
            <label style={S.label}>Session</label>
            <select style={S.select} value={sessionFilter} onChange={e => setSessionFilter(e.target.value)}>
              <option value="all">All Sessions</option>
              {sessions.map(session => (
                <option key={session.id} value={session.name}>{session.name}</option>
              ))}
            </select>
          </div>
          <div style={S.fg}>
            <label style={S.label}>Class</label>
            <select style={S.select} value={classFilter} onChange={e => setClassFilter(e.target.value)}>
              {classes.map(c => <option key={c} value={c}>{c === "all" ? "All Classes" : fmtClass(c)}</option>)}
            </select>
          </div>
          <div style={S.fg}>
            <label style={S.label}>Section</label>
            <select style={S.select} value={sectionFilter} onChange={e => setSectionFilter(e.target.value)}>
              {sections.map(s => <option key={s} value={s}>{s === "all" ? "All Sections" : s}</option>)}
            </select>
          </div>
          {/* Multi-select Fee Category */}
          <div ref={catDropRef} style={{ ...S.fg, position: "relative", minWidth: "200px", zIndex: catDropOpen ? 20 : 1 }}>
            <label style={S.label}>Fee Category</label>
            <button
              type="button"
              onClick={() => setCatDropOpen(o => !o)}
              style={{ ...S.select, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", cursor: "pointer", width: "100%", maxWidth: "260px", boxSizing: "border-box" }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, textAlign: "left" }}>
                {categoryFilters.size === 0
                  ? "All Categories"
                  : categoryFilters.size === 1
                    ? getCategoryLabel([...categoryFilters][0])
                    : `${categoryFilters.size} selected`}
              </span>
              <span style={{ fontSize: "10px", opacity: 0.6, flexShrink: 0 }}>{catDropOpen ? "▲" : "▼"}</span>
            </button>
            {catDropOpen && (
              <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 999, background: C.white, border: `1.5px solid ${C.border}`, borderRadius: "10px", boxShadow: "0 6px 24px rgba(0,0,0,0.12)", minWidth: "100%", width: "max-content", maxWidth: "320px", maxHeight: "260px", overflowY: "auto", padding: "8px 0", marginTop: "4px" }}>
                <div
                  style={{ padding: "7px 14px", fontSize: "13px", cursor: "pointer", color: categoryFilters.size === 0 ? C.blue : C.text, fontWeight: categoryFilters.size === 0 ? 700 : 400 }}
                  onClick={() => { setCategoryFilters(new Set()); setCatDropOpen(false); }}
                >All Categories</div>
                <div style={{ borderTop: `1px solid ${C.border}`, margin: "4px 0" }} />
                {categories.filter(c => c !== "all").map(cat => {
                  const checked = categoryFilters.has(cat);
                  return (
                    <div key={cat}
                      style={{ padding: "7px 14px", fontSize: "13px", cursor: "pointer", display: "flex", alignItems: "flex-start", gap: "8px", background: checked ? C.blueSoft : "transparent" }}
                      onClick={() => setCategoryFilters(prev => {
                        const n = new Set(prev);
                        checked ? n.delete(cat) : n.add(cat);
                        return n;
                      })}
                    >
                      <input type="checkbox" readOnly checked={checked} style={{ accentColor: C.blue, width: "14px", height: "14px", cursor: "pointer", marginTop: "2px", flexShrink: 0 }} />
                      <span style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.35 }}>{getCategoryLabel(cat)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{ ...S.fg, minWidth: "190px" }}>
            <label style={S.label}>Search</label>
            <input style={S.input} placeholder="Name, roll no, fee type…" value={searchQ} onChange={e => setSearchQ(e.target.value)} />
          </div>
        </div>

        {/* Action bar */}
        <div style={{ padding: "12px 20px", display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center", borderBottom: `1px solid ${C.border}`, background: C.white }}>
          <button
            onClick={handleBulkGenerate}
            disabled={classFilter === "all" && !someSelected}
            title="Open bulk challan page for this class/section"
            style={{
              ...S.btn(
                (classFilter !== "all" || someSelected) ? C.navy : C.bg,
                (classFilter !== "all" || someSelected) ? "#fff" : C.slate,
                (classFilter !== "all" || someSelected) ? "none" : `1px solid ${C.border}`
              ),
              opacity: (classFilter === "all" && !someSelected) ? 0.6 : 1,
              cursor: (classFilter === "all" && !someSelected) ? "not-allowed" : "pointer",
            }}
          >
            Bulk Generate
            {someSelected
              ? ` — ${selectedFiltered.length}`
              : classFilter !== "all"
                ? ` — ${fmtClass(classFilter)}${sectionFilter !== "all" ? ` / ${sectionFilter}` : ""}`
                : ""}
          </button>
          {someSelected && <button onClick={clearSelection} style={S.btn(C.bg, C.slate, `1px solid ${C.border}`)}>Clear Selection</button>}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
            <select value={perPage} onChange={e => setPerPage(Number(e.target.value))}
              style={{ ...S.select, padding: "5px 10px", fontSize: "12px" }} title="Rows per page">
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
            </select>
            <span style={{ fontSize: "12px", color: C.slate, fontWeight: 600, background: C.bg, padding: "5px 12px", borderRadius: "20px" }}>{filtered.length} of {allFeeItems.length} shown</span>
          </div>
        </div>
      </div>

      {/* Table card */}
      <div style={S.card}>
        {filtered.length === 0
          ? <div style={{ padding: "60px", textAlign: "center", color: C.slate }}>
              <div style={{ fontSize: "32px", marginBottom: "8px" }}>🔍</div>
              <div style={{ fontWeight: 600, fontSize: "15px", color: C.text, marginBottom: "4px" }}>No fee assignments match your filters</div>
              <div style={{ fontSize: "13px" }}>Try adjusting your search or filter criteria</div>
            </div>
          : <div style={{ overflowX: "auto" }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={{ ...S.th, width: "40px" }}><input type="checkbox" style={S.checkbox} checked={allSelected} onChange={toggleAll} /></th>
                    <th style={S.th}>#</th>
                    <th style={S.th}>Student</th>
                    <th style={S.th}>Class</th>
                    <th style={S.th}>Section</th>
                    <th style={S.th}>Roll No.</th>
                    <th style={S.th}>Fee Category</th>
                    <th style={S.th}>Net Payable</th>
                    <th style={S.th}>Challan</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map(({ id: itemId, student, feeStructure }, idx) => {
                    const rowNo = (safePage - 1) * perPage + idx + 1;
                    const comps      = feeStructure?.components || [];
                    const subtotal   = comps.reduce((s, c) => s + c.amount, 0);
                    const concAmt    = feeStructure?.concessionAmount || 0;
                    const prevDues   = loadPrevDues(student.id);
                    const net        = Math.max(0, subtotal - concAmt) + prevDues;
                    const hasComps     = comps.length > 0;
                    const isSel        = selected.has(itemId);
                    const rowBg        = isSel ? C.blueSoft : idx % 2 === 0 ? C.white : C.bg;
                    const status       = feeStructure.status || "Unpaid";
                    const isPaid       = status === "Paid" || status === "Waived";
                    const challan      = getChallanForItem(student, feeStructure);
                    const hasChallan   = !!challan;

                    return (
                      <tr key={itemId} style={{ background: rowBg, cursor: "pointer" }}
                        onClick={() => navigate(`/student-fee-details/${student.id}`)}
                        title="Click to view student fee details"
                        onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = C.blueSoft; }}
                        onMouseLeave={e => { e.currentTarget.style.background = rowBg; }}>
                        <td style={S.td} onClick={e => e.stopPropagation()}><input type="checkbox" style={S.checkbox} checked={isSel} onChange={() => toggleOne(itemId)} /></td>
                        <td style={{ ...S.td, color: C.slateLight, fontWeight: 600 }}>{rowNo}</td>
                        <td style={{ ...S.td, fontWeight: 700 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.navy, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{student.name?.charAt(0)?.toUpperCase() || "?"}</div>
                            {student.name}
                          </div>
                        </td>
                        <td style={S.td}><span style={S.badge(C.blueSoft, C.blue)}>{fmtClass(student.class)}</span></td>
                        <td style={{ ...S.td, fontWeight: 600 }}>{fmtSection(student.section) || "—"}</td>
                        <td style={{ ...S.td, fontFamily: "monospace", fontWeight: 600 }}>{student.rollNo}</td>
                        <td style={S.td}><span style={S.badge(C.amberSoft, C.amber)}>{feeStructure.categoryLabel}</span></td>

                        <td style={S.td}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                            <span style={S.badge(C.greenSoft, C.green)}>{hasComps ? fmtPKR(net) : <span style={{ color: C.slateLight }}>No components</span>}</span>
                            {prevDues > 0 && <span style={{ fontSize: "10px", color: C.red, fontWeight: 600 }}>incl. {fmtPKR(prevDues)} dues</span>}
                          </div>
                        </td>
                        <td style={S.td} onClick={e => e.stopPropagation()}>
                          {hasChallan ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                              <span style={S.badge(C.greenSoft, C.green)}>Generated</span>
                              <span style={{ fontSize: "10px", color: C.slate, fontFamily: "monospace" }}>{challan.challanNumber}</span>
                              {!isPaid && (
                                <button
                                  onClick={() => setEditChallan({
                                    item: { student, feeStructure },
                                    challan,
                                    selectedCats: new Set(challan.selectedCategories || feeStructure.selectedCategories || [feeStructure.categoryId].filter(Boolean)),
                                    dueDate: challan.dueDate || feeStructure.dueDate || "10",
                                    components: (challan.components || feeStructure.components || []).map((c) => ({ ...c })),
                                    netPayable: challan.netPayable || feeStructure.netPayable || net,
                                  })}
                                  style={{ ...S.btn(C.bg, C.blue, `1px solid ${C.border}`), padding: "3px 8px", fontSize: "11px" }}
                                >
                                  ✎ Edit
                                </button>
                              )}
                            </div>
                          ) : (
                            <span style={{ fontSize: "12px", color: C.slateLight, fontWeight: 600 }}>Not generated</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${C.border}`, flexWrap: "wrap", gap: "10px" }}>
                  <span style={{ fontSize: "12px", color: C.slate }}>
                    Showing {(safePage - 1) * perPage + 1}–{Math.min(safePage * perPage, filtered.length)} of {filtered.length}
                  </span>
                  <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                    <button onClick={() => setPage(safePage - 1)} disabled={safePage === 1}
                      style={{ ...S.btn(C.bg, C.slate, `1px solid ${C.border}`), padding: "5px 12px", fontSize: "12px", opacity: safePage === 1 ? 0.5 : 1, cursor: safePage === 1 ? "not-allowed" : "pointer" }}>← Prev</button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                      .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push("…"); acc.push(p); return acc; }, [])
                      .map((p, i) => p === "…"
                        ? <span key={`e${i}`} style={{ padding: "5px 4px", fontSize: "12px", color: C.slateLight }}>…</span>
                        : <button key={p} onClick={() => setPage(p)}
                            style={{ padding: "5px 10px", fontSize: "12px", fontWeight: 600, borderRadius: "6px", border: p === safePage ? "none" : `1px solid ${C.border}`, background: p === safePage ? C.navy : C.white, color: p === safePage ? "#fff" : C.text, cursor: "pointer" }}>{p}</button>
                      )}
                    <button onClick={() => setPage(safePage + 1)} disabled={safePage === totalPages}
                      style={{ ...S.btn(C.bg, C.slate, `1px solid ${C.border}`), padding: "5px 12px", fontSize: "12px", opacity: safePage === totalPages ? 0.5 : 1, cursor: safePage === totalPages ? "not-allowed" : "pointer" }}>Next →</button>
                  </div>
                </div>
              )}
            </div>
        }
      </div>
      {/* ── Edit Generated Challan Modal ─────────────────────────────────── */}
      {editChallan && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,29,54,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
          onClick={() => setEditChallan(null)}>
          <div style={{ background: C.white, borderRadius: "16px", width: "100%", maxWidth: "460px", boxShadow: "0 16px 48px rgba(0,0,0,0.22)", overflow: "hidden" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ background: C.navy, padding: "18px 22px" }}>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "16px", color: "#fff" }}>Edit Challan</div>
              <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.65)", marginTop: "3px" }}>
                {editChallan.item.student.name} · {editChallan.challan.challanNumber}
              </div>
            </div>
            <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: "16px" }}>
              <div>
                <div style={S.label}>Due Date (day of month)</div>
                <input type="number" min="1" max="31" value={editChallan.dueDate}
                  onChange={e => setEditChallan(p => ({ ...p, dueDate: e.target.value }))}
                  style={{ ...S.input, marginTop: "6px" }} />
              </div>
              <div>
                <div style={S.label}>Fee Categories</div>
                <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "6px" }}>
                  {FEE_CATEGORIES.filter(c => c.id !== "custom").map(cat => {
                    const checked = editChallan.selectedCats.has(cat.id);
                    return (
                      <label key={cat.id} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: "pointer" }}>
                        <input type="checkbox" checked={checked}
                          onChange={() => setEditChallan(p => {
                            const n = new Set(p.selectedCats);
                            checked ? n.delete(cat.id) : n.add(cat.id);
                            const components = buildComponentsFromCategories([...n], p.components || []);
                            const subtotal = components.reduce((s, c) => s + (Number(c.amount) || 0), 0);
                            return { ...p, selectedCats: n, components, netPayable: subtotal };
                          })}
                          style={{ accentColor: C.blue }} />
                        {cat.label}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <div style={S.label}>Fee Components</div>
                <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "8px" }}>
                  {(editChallan.components || []).length === 0 ? (
                    <span style={{ fontSize: "12px", color: C.slate }}>Select at least one category</span>
                  ) : editChallan.components.map((comp, idx) => (
                    <div key={comp.componentId || idx} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ flex: 1, fontSize: "13px" }}>{comp.componentLabel}</span>
                      <input
                        type="number"
                        min="0"
                        value={comp.amount}
                        onChange={(e) => setEditChallan((p) => {
                          const components = (p.components || []).map((c, i) =>
                            i === idx ? { ...c, amount: Number(e.target.value) || 0 } : c
                          );
                          const subtotal = components.reduce((s, c) => s + (Number(c.amount) || 0), 0);
                          return { ...p, components, netPayable: subtotal };
                        })}
                        style={{ ...S.input, width: "110px", padding: "6px 8px" }}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ background: C.greenSoft, borderRadius: "10px", padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "13px", fontWeight: 700, color: C.green }}>Net Payable</span>
                <span style={{ fontSize: "18px", fontWeight: 800, color: C.green }}>{fmtPKR(editChallan.netPayable || 0)}</span>
              </div>
            </div>
            <div style={{ padding: "16px 22px", borderTop: `1px solid ${C.border}`, display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button onClick={() => setEditChallan(null)} style={{ ...S.btn(C.bg, C.slate, `1px solid ${C.border}`), padding: "9px 20px" }}>Cancel</button>
              <button onClick={handleSaveChallanEdit} style={{ ...S.btn(C.navy, "#fff"), padding: "9px 20px" }}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChallanGenerationView;
